import { useEffect, useRef } from "react";
import classNames from "classnames";
import SocialButtons from "../SocialButtons";
import { FormikProvider, useFormik } from "formik";
import FormikInput from "../FormikInput";
import {
  MAX_EMAIL_LENGTH,
  MAX_PASSWORD_LENGTH,
  MAX_USERNAME,
  MIN_PASSWORD_LENGTH,
} from "@validation/lengthConstants";
import FormikPassword from "../FormikPassword";
import FormikCheckbox from "../FormikCheckbox";
import { Link } from "react-router-dom";
import { Button } from "../Button";
import { useDispatch, useSelector } from "react-redux";
import { localstorageKeys } from "@const/store";
import { signUp, signUpReset } from "@redux/signUp/actions";
import { initialValues, validationSchema } from "./form";
import { ErrorCodes } from "@const/errors";
import { signInReset, toggleSignInModal } from "@redux/signIn/actions";
import { useService } from "@/base/hooks/useService";
import { StorageService } from "@services/StorageService";
import { ToasterService } from "@services/ToastService";
import { toggleBlockedUserModal } from "@redux/blockedUserModal/actions";
import styles from "./index.module.scss";
import routes from "../../const/routes";
import { Trans, useTranslation } from "react-i18next";

/**
 * @param {Object} arguments
 * @param {boolean} arguments.dataSent
 * @param {boolean} arguments.isVisible
 * @param {Function} arguments.setSignUpStep
 * @param {Function} arguments.setEmail
 * @param {Function} arguments.setPassword
 * */

const FirstStep = ({
  dataSent,
  isVisible,
  setEmail,
  setPassword,
  setSignUpStep,
}) => {
  const { t } = useTranslation("sign-up");
  const { t: t1 } = useTranslation("terms-of-use");
  const { t: t2 } = useTranslation("sign-in");
  const { t: t3 } = useTranslation("common");
  const { t: t4 } = useTranslation("buttons");
  const { t: t5 } = useTranslation("standard-contract");
  const storageService = useService(StorageService);
  const dispatch = useDispatch();
  const prevState = useRef("");
  const toasterService = useService(ToasterService);

  const { isLoading, error } = useSelector(
    ({ signUpReducer }) => signUpReducer,
  );

  const { isLoading: isSocialsLoading, error: signInError } = useSelector(
    ({ signInReducer }) => signInReducer,
  );

  const returnStringifiedState = (username, password, email) =>
    `${email}-${password}-${username}`;

  const handleSubmit = ({
    username,
    email,
    password,
    isAcceptedTermsAndPrivacy,
    isAdultConfirmation,
  }) => {
    const newState = returnStringifiedState(username, password, email.trim());
    // if the data is already sent, we don't need to send it again
    if (dataSent && prevState.current === newState) {
      prevState.current = newState;
      setSignUpStep(false);
      return;
    }

    const validEmail = email.trim();
    setEmail(validEmail);
    setPassword(password);

    prevState.current = returnStringifiedState(username, password, validEmail);

    const referral = storageService.get(
      localstorageKeys.creatorReferral,
      {},
    ).newCreatorReferralCode;

    const trackingCode = storageService.get(localstorageKeys.trackingCode, "");

    dispatch(
      signUp({
        username,
        email: validEmail,
        password,
        referral,
        isAcceptedTermsAndPrivacy,
        isAdultConfirmation,
        trackingCode,
      }),
    );
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  const openLoginModal = () => {
    dispatch(signUpReset());
    dispatch(toggleSignInModal(true));
  };

  useEffect(() => {
    if (error) {
      toasterService.error(error.message);
      dispatch(
        signUpReset({
          isModalVisible: true,
        }),
      );
    } else if (signInError) {
      switch (signInError?.code) {
        case ErrorCodes.FORBIDDEN_ERROR:
          dispatch(signUpReset());
          dispatch(toggleBlockedUserModal(true));
          break;
        default:
          if (error?.code) {
            toasterService.error(error?.message);
          }
          break;
      }
      dispatch(signInReset());
    }
  }, [error, signInError]);

  const signUpDisabled =
    isLoading ||
    isSocialsLoading ||
    (Object.values(formik?.touched || {}).length && !formik.isValid);

  return (
    <div
      style={{
        display: isVisible ? "block" : "none",
      }}
    >
      <div
        className={classNames(
          "container h-100 d-flex flex-column justify-content-center",
          styles.container,
        )}
      >
        <div
          className={classNames("text-center h3 text-primary", styles.title)}
        >
          {t("TITLE")}
        </div>
        <div className={classNames("mx-auto", styles.formWrapper)}>
          <span
            className={classNames(
              "text-tertiary text-body-1 text-align-center d-block",
              styles.withAccountText,
            )}
          >
            {t2("WITH_ACCOUNT")}
          </span>
          <SocialButtons
            isAdultConfirmation={formik.values.isAdultConfirmation}
            isAcceptedTermsAndPrivacy={formik.values.isAcceptedTermsAndPrivacy}
          />
          <div
            className={classNames(
              "d-flex align-items-center justify-content-center",
              styles.orContainer,
            )}
          >
            <div className={styles.orLine} />
            <div
              className={classNames(
                styles.sectionsDivider,
                "text-center text-tertiary text-body-1",
              )}
            >
              {t3("OR")}
            </div>
            <div className={styles.orLine} />
          </div>

          <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit}>
              <FormikInput
                maxLength={MAX_USERNAME}
                label="USERNAME"
                placeholder="USERNAME"
                name="username"
                autocomplete="username"
              />
              <FormikInput
                maxLength={MAX_EMAIL_LENGTH}
                label="EMAIL"
                placeholder="EMAIL"
                name="email"
                autocomplete="email"
              />
              <FormikPassword
                type="password"
                label="CREATE_PWD"
                name="password"
                min={MIN_PASSWORD_LENGTH}
                max={MAX_PASSWORD_LENGTH}
                placeholder="PASSWORD"
                maxLength={MAX_PASSWORD_LENGTH}
                wrapperClassName={styles.passwordWrapper}
              />
              <FormikCheckbox
                name="isAcceptedTermsAndPrivacy"
                wrapperClassName={styles.formCheckboxWrapper}
                contentClassName={styles.formCheckboxContent}
                className={styles.formCheckbox}
                errorClassName={styles.errorClassName}
              >
                <div className="text-regular text-body-2 text-tertiary">
                  {t3("I_ACCEPT")}{" "}
                  <Link
                    to={routes.TERMS_OF_USE()}
                    className="link-secondary"
                    target="_blank"
                  >
                    {t1("TITLE")}
                  </Link>{" "}
                  {t3("AND")}{" "}
                  <Link
                    to={routes.STANDARD_CONTRACT()}
                    className="link-secondary"
                    target="_blank"
                  >
                    {t5("SHORT_TITLE")}
                  </Link>
                </div>
              </FormikCheckbox>
              <FormikCheckbox
                name="isAdultConfirmation"
                wrapperClassName={styles.formCheckboxWrapper}
                contentClassName={styles.formCheckboxContent}
                className={styles.formCheckbox}
                errorClassName={styles.errorClassName}
              >
                <div className="text-regular text-body-2 text-tertiary">
                  {t3("ADULT_CONTENT")}{" "}
                </div>
              </FormikCheckbox>
              <section className="form-control-wrapper">
                <div
                  className={classNames(
                    "text-regular text-body-2 text-tertiary",
                    styles.privacyText,
                  )}
                >
                  <Trans
                    t={t3}
                    components={{
                      a: (
                        <Link
                          to={routes.PRIVACY_POLICY()}
                          className="link-secondary"
                          target="_blank"
                        />
                      ),
                    }}
                    i18nKey="ADULT_PRIVACY"
                  />
                </div>
              </section>
              <Button
                size="lg"
                type="submit"
                color="primary"
                className="w-100"
                disabled={signUpDisabled}
              >
                {t4("SIGN_UP")}
              </Button>
            </form>
          </FormikProvider>
        </div>
        <div
          className={classNames(
            "text-tertiary text-center text-regular text-body-2",
            styles.alreadyHaveAccount,
          )}
        >
          {t3("ALREADY_HAVE_ACC")}{" "}
          <span
            onClick={openLoginModal}
            className="cursor-pointer text-underline text-medium text-body-semi-bold-1 link-primary text-decoration-none"
          >
            {t4("LOG_IN")}
          </span>
        </div>
      </div>
    </div>
  );
};

export default FirstStep;
